import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, auth as authApi } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast, Toaster } from "sonner";
import { Loader2, Lock, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const { lang } = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error(lang === "fr" ? "Au moins 6 caractères" : "At least 6 characters"); return; }
    if (pw !== pw2) { toast.error(lang === "fr" ? "Les mots de passe ne correspondent pas" : "Passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      authApi.setToken(data.access_token);
      authApi.setUser(data.user);
      toast.success(lang === "fr" ? "Mot de passe mis à jour" : "Password updated");
      window.location.href = "/account";
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "fr" ? "Lien invalide ou expiré" : "Invalid or expired link"));
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-5 md:px-10 py-16 text-center" data-testid="reset-no-token">
        <h1 className="font-display text-3xl text-[#1F3A5F]">{lang === "fr" ? "Lien manquant" : "Missing link"}</h1>
        <p className="text-sm text-[#4A5568] mt-3">{lang === "fr" ? "Veuillez utiliser le lien reçu par email." : "Please use the link from your email."}</p>
        <Link to="/forgot-password" className="mt-6 inline-block px-6 py-3 rounded-xl bg-[#E5276A] text-white font-bold">
          {lang === "fr" ? "Demander un nouveau lien" : "Request a new link"}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="reset-password-page">
      <Toaster theme="light" />
      <span className="chip-pink">{lang === "fr" ? "Nouveau mot de passe" : "New password"}</span>
      <h1 className="font-display text-4xl text-[#1F3A5F] tracking-tight mt-4 font-medium mb-8">
        {lang === "fr" ? "Définir un nouveau mot de passe" : "Set a new password"}
      </h1>
      <form onSubmit={submit} className="space-y-5 p-6 md:p-8 rounded-2xl border border-[#1F3A5F]/10 bg-white shadow-sm">
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">{lang === "fr" ? "Nouveau mot de passe" : "New password"}</span>
          <div className="relative">
            <input
              data-testid="reset-pw-input"
              type={show ? "text" : "password"} required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)}
              className="w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] focus:ring-2 focus:ring-[#E5276A]/10 outline-none rounded-lg px-4 py-3 pr-12 text-sm text-[#1F3A5F]"
            />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B95A1] hover:text-[#1F3A5F]" data-testid="reset-pw-toggle">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">{lang === "fr" ? "Confirmer" : "Confirm"}</span>
          <input
            data-testid="reset-pw2-input"
            type={show ? "text" : "password"} required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)}
            className="w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] focus:ring-2 focus:ring-[#E5276A]/10 outline-none rounded-lg px-4 py-3 text-sm text-[#1F3A5F]"
          />
        </label>
        <button
          data-testid="reset-submit"
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#E5276A] hover:bg-[#C01661] disabled:opacity-50 text-white text-base font-medium transition-colors"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
          {lang === "fr" ? "Définir le mot de passe" : "Set password"}
        </button>
      </form>
    </div>
  );
}
